import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase, onAuthStateChange, getCurrentUser } from '../services/supabase'

const SupabaseContext = createContext()

export const useSupabase = () => {
  const context = useContext(SupabaseContext)
  if (!context) {
    throw new Error('useSupabase must be used within a SupabaseProvider')
  }
  return context
}

export const SupabaseProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        const { user: currentUser } = await getCurrentUser()
        setUser(currentUser)
        setIsAuthenticated(!!currentUser)
        
        if (currentUser) {
          // Fetch user profile
          const { data: profileData } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', currentUser.id)
            .single()
          
          setProfile(profileData)
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
      } finally {
        setLoading(false)
      }
    }

    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = onAuthStateChange((event, session) => {
      const currentUser = session?.user || null
      setUser(currentUser)
      setIsAuthenticated(!!currentUser)
      
      if (currentUser) {
        // Fetch or create user profile
        const upsertProfile = async () => {
          const { data: profileData } = await supabase
            .from('profiles')
            .upsert({
              id: currentUser.id,
              username: currentUser.user_metadata?.username || currentUser.email?.split('@')[0],
              avatar_url: null,
              updated_at: new Date().toISOString()
            })
            .select()
            .single()
          
          setProfile(profileData)
        }
        upsertProfile()
      } else {
        setProfile(null)
      }
    })

    return () => {
      subscription?.unsubscribe()
    }
  }, [])

  const value = {
    user,
    profile,
    loading,
    isAuthenticated,
    setUser,
    setProfile,
    supabase
  }

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  )
}
